"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const floatingCards = [
{
  id: "card-health",
  title: "Health Check",
  subtitle: "Luna · Golden Retriever",
  value: "All Good",
  valueColor: "#22C55E",
  icon: "HeartIcon",
  iconBg: "#E8F7F6",
  iconColor: "#5EBFB3",
  animClass: "animate-float-a",
  position: "top-[10%] right-[2%] md:right-[5%]",
  delay: "0s"
},
{
  id: "card-vet",
  title: "Connected Vet",
  subtitle: "Dr. Sarah · Verified",
  value: "Profile Shared",
  valueColor: "#5EBFB3",
  icon: "UserGroupIcon",
  iconBg: "#FEF9C3",
  iconColor: "#CA8A04",
  animClass: "animate-float-b",
  position: "bottom-[20%] right-[0%] md:right-[3%]",
  delay: "1.2s"
},
{
  id: "card-reminder",
  title: "Upcoming",
  subtitle: "Tomorrow, 10:00 AM",
  value: "Vet Checkup",
  valueColor: "#7C3AED",
  icon: "BellIcon",
  iconBg: "#EDE9FE",
  iconColor: "#7C3AED",
  animClass: "animate-float-c",
  position: "top-[42%] right-[18%] md:right-[20%]",
  delay: "0.6s"
}];


export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll(".hero-animate");
    items.forEach((item, i) => {
      (item as HTMLElement).style.animationDelay = `${i * 0.12}s`;
      (item as HTMLElement).style.animationFillMode = "forwards";
      item.classList.add("hero-reveal");
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#E8F8F7] via-[#F0FAFB] to-[#E0F2FE] dark:from-[#0f172a] dark:via-[#0f2027] dark:to-[#0f172a]"
    >
      <style>{`
        .dark section[class*="min-h-screen"] {
          background: linear-gradient(145deg, #0f172a 0%, #0f2027 45%, #0f172a 100%) !important;
        }
      `}</style>
      
      {/* Background blobs */}
      <div
        className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(14,165,160,0.15) 0%, transparent 70%)" }} />
      
      <div
        className="absolute bottom-[-5%] right-[-8%] w-[600px] h-[600px] rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)" }} />
      

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-16 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <div className="flex flex-col gap-7 z-10">
            <div
              className="hero-animate opacity-0"
              style={{ animation: "fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s forwards" }}>
              
              <span className="teal-badge">
                <span className="status-dot" />
                Beta now open · 2,400+ pets registered
              </span>
            </div>

            <div
              className="hero-animate opacity-0"
              style={{ animation: "fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.22s forwards" }}>
              
              <h1 className="text-5xl sm:text-6xl lg:text-[64px] font-extrabold leading-[1.08] tracking-tight text-[#1A2B2B] dark:text-slate-100">
                Your pet's whole life,{" "}
                <span className="gradient-text">in one place.</span>
              </h1>
            </div>

            <div
              className="hero-animate opacity-0"
              style={{ animation: "fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.34s forwards" }}>
              
              <p className="text-lg text-[#4A6363] dark:text-slate-400 leading-relaxed max-w-[520px]">
                DogFob - Because Life's Ruff Without It.
              </p>
            </div>

            <div
              className="hero-animate opacity-0 flex flex-col sm:flex-row gap-3"
              style={{ animation: "fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.46s forwards" }}>
              
              <Link href="/for-pet-owners" className="btn-primary text-base py-3.5 px-7 transition-all duration-200 hover:scale-105 hover:shadow-lg">
                <Icon name="UserCircleIcon" size={20} />
                For Pet Owners
              </Link>
              <Link href="/for-businesses" className="btn-secondary text-base py-3.5 px-7 transition-all duration-200 hover:scale-105 hover:shadow-lg">
                <Icon name="BuildingOfficeIcon" size={20} />
                For Pet Businesses
              </Link>
            </div>

            <div
              className="hero-animate opacity-0 flex items-center gap-6 pt-2"
              style={{ animation: "fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.58s forwards" }}>
              
              {[
              { label: "Free forever plan", icon: "CheckCircleIcon" },
              { label: "No credit card needed", icon: "CheckCircleIcon" },
              { label: "Works with all GPS tags", icon: "CheckCircleIcon" }].
              map((item) =>
              <div key={item.label} className="flex items-center gap-1.5">
                  <Icon name={item.icon as "CheckCircleIcon"} size={16} className="text-teal-500" variant="solid" />
                  <span className="text-xs font-medium text-[#4A6363] dark:text-slate-400">{item.label}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Visual */}
          <div className="relative h-[520px] lg:h-[600px] flex items-center justify-center">
            {/* Central phone mockup */}
            <div
              className="relative z-10 opacity-0"
              style={{ animation: "scaleIn 0.8s cubic-bezier(0.22,1,0.36,1) 0.4s forwards" }}>
              
              <div
                className="w-[260px] sm:w-[290px] rounded-[36px] overflow-hidden"
                style={{
                  boxShadow: "0 32px 80px -16px rgba(14,165,160,0.3), 0 0 0 1px rgba(14,165,160,0.12)"
                }}>
                
                <AppImage
                  src="https://images.unsplash.com/photo-1709424719811-db78154be0ae"
                  alt="Happy golden retriever dogfob running in a sunny park, representing DogFob pet tracking"
                  width={290}
                  height={400}
                  className="w-full h-[400px] object-cover"
                  priority />
                
                {/* App overlay bar */}
                <div className="bg-white dark:bg-[#1e293b] px-5 py-4 border-t border-[#E2EDED] dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#8AA4A4] dark:text-slate-500 font-medium">Active pet</p>
                      <p className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">Luna · 3 yrs</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="status-dot" />
                      <span className="text-xs font-semibold text-[#22C55E]">Safe</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating UI cards */}
            {floatingCards.map((card) =>
            <div
              key={card.id}
              className={`absolute ${card.position} ${card.animClass} z-20`}
              style={{ animationDelay: card.delay }}>
              
                <div
                className="glass-card rounded-2xl px-4 py-3 min-w-[160px] shadow-float"
                style={{ boxShadow: "0 12px 40px rgba(15,32,39,0.1)" }}>
                
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: card.iconBg }}>
                    
                      <Icon name={card.icon as "HeartIcon"} size={14} style={{ color: card.iconColor }} />
                    </div>
                    <span className="text-[11px] font-semibold text-[#4A6363] dark:text-slate-300 font-heading">{card.title}</span>
                  </div>
                  <p className="text-[10px] text-[#8AA4A4] dark:text-slate-400 mb-0.5">{card.subtitle}</p>
                  <p className="text-sm font-bold font-heading" style={{ color: card.valueColor }}>
                    {card.value}
                  </p>
                </div>
              </div>
            )}

            {/* Second pet peek */}
            <div
              className="absolute bottom-[5%] left-[2%] z-10 animate-float-b opacity-0"
              style={{ animation: "scaleIn 0.7s cubic-bezier(0.22,1,0.36,1) 0.65s forwards, floatB 8s 1.2s cubic-bezier(0.45,0.05,0.55,0.95) infinite" }}>
              
              <div
                className="w-[110px] h-[110px] rounded-[24px] overflow-hidden border-4 border-white dark:border-slate-700"
                style={{ boxShadow: "0 12px 40px rgba(15,32,39,0.12)" }}>
                
                <AppImage
                  src="https://images.unsplash.com/photo-1543852786-1cf6624b9987"
                  alt="Cute tabby cat sitting indoors, representing cat owner features in DogFob"
                  width={110}
                  height={110}
                  className="w-full h-full object-cover" />
                
              </div>
              <div className="mt-1.5 text-center">
                <p className="text-[11px] font-bold text-[#1A2B2B] dark:text-slate-300 font-heading">Mochi · Cat</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}